import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { checkRegistrationStatusByEmail, type PublicRegistrationStatus } from '@/api/academy-registration';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';

const THEME = ROLE_LOGIN_THEMES['academy_status'];

type PageState = 'form' | 'loading' | 'result' | 'error';

const STATUS_LABELS: Record<string, string> = {
  pending_otp: 'OTP Verification Pending',
  pending_approval: 'Pending Admin Approval',
  approved: 'Approved',
  rejected: 'Not Approved',
  info_requested: 'More Information Requested',
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  pending_otp: 'Email verification is still pending. Please complete the OTP step to submit your application.',
  pending_approval: 'Your registration is under review by our platform team. You will be notified once a decision is made.',
  approved: 'Your academy has been approved! You can now log in as an Academy Admin.',
  rejected: 'Your registration was not approved at this time. See the admin notes below for details.',
  info_requested: 'Our team has requested additional information. Please review the notes below and contact support.',
};

const STATUS_ICON_TYPE: Record<string, string> = {
  pending_otp: 'pending', pending_approval: 'pending', approved: 'approved',
  rejected: 'rejected', info_requested: 'info',
};

function StatusIcon({ type }: { type: string }) {
  const icons: Record<string, JSX.Element> = {
    pending: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" />
      </svg>
    ),
    approved: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    rejected: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
    info: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  };
  return <div className={`rl__status-icon rl__status-icon--${type}`}>{icons[type] ?? icons.pending}</div>;
}

export function RegistrationStatusPage() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<PublicRegistrationStatus | null>(null);
  const [error, setError] = useState('');

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) { setError('Please enter a valid email address.'); return; }
    setError(''); setPageState('loading');
    try {
      const data = await checkRegistrationStatusByEmail(email.trim());
      setResult(data); setPageState('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status check failed');
      setPageState('error');
    }
  };

  const handleReset = () => { setPageState('form'); setResult(null); setError(''); };

  const iconType = result ? (STATUS_ICON_TYPE[result.status] ?? 'pending') : 'pending';

  const meta = pageState === 'form' ? (
    <>
      <p className="rl__meta-row">New registration? <Link to="/register/academy">Register an Academy</Link></p>
      <p className="rl__meta-row">Already approved? <Link to="/login">Sign in</Link></p>
    </>
  ) : undefined;

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      {pageState === 'form' && (
        <form className="rl__form" onSubmit={handleCheck}>
          {error && <div className="rl__error" role="alert">{error}</div>}
          <div className="rl__field">
            <label className="rl__label" htmlFor="status-email">Registered Email Address</label>
            <input id="status-email" className="rl__input" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter the email you registered with"
              required autoFocus autoComplete="email" />
          </div>
          <p className="rl__otp-note">Enter the email address used during your academy registration to check its current status.</p>
          <button type="submit" className="rl__submit">Check Status</button>
        </form>
      )}

      {pageState === 'loading' && (
        <div className="rl__form rl__form--center">
          <svg className="rl__spin" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <p className="rl__otp-note">Checking registration status...</p>
        </div>
      )}

      {pageState === 'result' && result && (
        <div className="rl__form">
          <StatusIcon type={iconType} />
          <h2 className="rl__status-title">{STATUS_LABELS[result.status] ?? result.status}</h2>
          <p className="rl__status-desc">{STATUS_DESCRIPTIONS[result.status] ?? ''}</p>
          <div className="rl__status-details">
            <div className="rl__status-row">
              <span className="rl__status-key">Academy</span>
              <span className="rl__status-val">{result.academy_name}</span>
            </div>
            <div className="rl__status-row">
              <span className="rl__status-key">Email</span>
              <span className="rl__status-val">{email}</span>
            </div>
            <div className="rl__status-row">
              <span className="rl__status-key">Submitted</span>
              <span className="rl__status-val">
                {new Date(result.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            {result.reviewed_at && (
              <div className="rl__status-row">
                <span className="rl__status-key">Reviewed</span>
                <span className="rl__status-val">
                  {new Date(result.reviewed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
          {result.admin_notes && (
            <div className="rl__status-notes">
              <div className="rl__status-notes-label">Admin Notes</div>
              <p>{result.admin_notes}</p>
            </div>
          )}
          {result.status === 'approved' ? (
            <Link to="/login" className="rl__submit-link">Go to Login</Link>
          ) : (
            <button type="button" className="rl__submit" onClick={handleReset}>Check Another Email</button>
          )}
        </div>
      )}

      {pageState === 'error' && (
        <div className="rl__form">
          <div className="rl__status-icon rl__status-icon--rejected">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 className="rl__status-title">Not Found</h2>
          <p className="rl__status-desc">{error}</p>
          <button type="button" className="rl__submit" onClick={handleReset}>Try Again</button>
          <div className="rl__otp-actions rl__mt-sm">
            <Link to="/register/academy" className="rl__link-btn">Register an Academy</Link>
          </div>
        </div>
      )}
    </RoleLoginShell>
  );
}
